import { html, css, LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import classNames from 'classnames'
import _ from 'lodash'
import type { SupportedLanguage } from '../i18n/i18n.no-deps'
import { noSearchResults } from './helpers/noSearchResults'
import './zaptic-checkbox'
import './zaptic-assignable-icon'
import type { Tag } from '../domain/tags'

type Events = 'onHovered' | 'onSelected'

@customElement('zaptic-tag-list')
export class TagList extends LitElement {
    public static tagName = 'zaptic-tag-list'
    public events: Events[] = ['onHovered', 'onSelected']

    static styles = css`
        :host {
            height: 0;
            overflow: visible;
            height: 100%;
        }

        .above {
            transform: translate(0, -100%);
        }

        .tag-list {
            position: relative;
            margin: 0;
            padding: 0;
            max-height: 250px;
            overflow-y: scroll;
            height: 100%;
        }

        .list-item {
            display: flex;
            align-items: center;
            font-weight: var(--font-weight-medium);
            font-size: 14px;
            padding: 10px 15px;
            border-radius: 5px;
            cursor: pointer;
        }

        .tag-focus {
            background: var(--disabled);
        }

        .tag-details {
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
        }

        .tag-secondary-text {
            margin-top: 2px;
            color: var(--grey3);
        }

        .not-found {
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 2.5rem 4.5rem;
            height: 100%;
            max-height: 20rem;
        }
        .not-found-text {
            padding-bottom: 1rem;
            color: var(--grey5);
            font-weight: var(--font-weight-medium);
            font-size: var(--small);
        }
    `

    public scrollSelectedIntoView() {
        const selected = (this.shadowRoot.querySelector('.tag-focus') ||
            this.shadowRoot.querySelector('.list-tag')) as HTMLElement
        selected?.scrollIntoView?.({ block: 'center' })
    }

    @property({ converter: (value) => JSON.parse(value) })
    selected: number

    @property({ converter: (value) => JSON.parse(value) })
    tags: Tag[] = []

    @property({ converter: (value) => JSON.parse(value) })
    language: SupportedLanguage

    @property({ converter: (value) => JSON.parse(value) })
    'selected-tags': Tag[] = []

    private handleHover(index: number) {
        return () => {
            this.dispatchEvent(new CustomEvent('onHovered', { detail: index }))
        }
    }

    private handleClick(index: number) {
        return () => {
            this.dispatchEvent(new CustomEvent('onSelected', { detail: index }))
        }
    }

    private isTagSelected = (tag: Tag) => {
        return this['selected-tags'] ? _.some(this['selected-tags'], tag) : false
    }

    private tagHtml(tag: Tag, index: number, selected: boolean) {
        const tagHtml = html`<div class="tag-details">
            <span class="tag-primary-text"><zaptic-tag label=${tag.name}></zaptic-tag></span>
        </div>`

        return html`
            <li
                class=${classNames('list-item', { 'tag-focus': selected })}
                @mouseover=${this.handleHover(index)}
                @focus=${this.handleHover(index)}
                @click=${this.handleClick(index)}
                tabindex="-1"
            >
                <zaptic-checkbox
                    .selected=${this.isTagSelected(tag)}
                    @onChange=${null}
                ></zaptic-checkbox>
                ${tagHtml}
                </div>
            </li>
        `
    }

    private tagListHtml(tags: Tag[], selectedIndex: number) {
        return html`
            <ul class="tag-list">
                ${tags.map((tag, idx) => this.tagHtml(tag, idx, selectedIndex === idx))}
            </ul>
        `
    }

    render() {
        if (this.tags == null || this.language == null || this.selected == null) {
            return
        }

        return this.tags.length === 0 ? noSearchResults(this.language) : this.tagListHtml(this.tags, this.selected)
    }
}
